import { useCallback, useEffect, useRef, useState } from "react";
import { FiRefreshCw } from "react-icons/fi";
import { getApiUrl } from "../../../config/api";
import {
  cycleNumber,
  cyclesUpTo,
  formatShortDay,
  formatTwdFromUsd,
  formatUsdWithCode,
} from "../../../components/usageBilling/usageFormat";
import useUsdTwdRate from "../../../components/usageBilling/useUsdTwdRate";
import UsageCurrencySwitch from "../../../components/usageBilling/UsageCurrencySwitch";
import EnforcementCard from "./usageBilling/EnforcementCard";
import UsageTotals from "./usageBilling/UsageTotals";
import GroupSpendTable from "./usageBilling/GroupSpendTable";
import BotSpendTable from "./usageBilling/BotSpendTable";
import UsageUsersTable from "./usageBilling/UsageUsersTable";
import UserUsageModal from "./usageBilling/UserUsageModal";
import UserPickerModal from "./usageBilling/UserPickerModal";
import UnpricedCallsTable from "./usageBilling/UnpricedCallsTable";

// LLM usage billing for admins: one 28-day cycle's spend, per group, per
// user and per website chatbot, what went unpriced, and the controls - group
// limits, per-user overrides, chatbot limits and the enforcement switch. Shown with billing.view_all; the limit
// controls need billing.limits.manage and the switch billing.enforcement.manage
// (the backend enforces the same split). Loads its own data when the tab opens,
// so the Admin page's first load isn't slowed by it.
export default function UsageBillingPanel({ language, currentUser, hasRole }) {
  const zh = language === "zh";
  const [currency, setCurrency] = useState("USD");
  const rateState = useUsdTwdRate();
  const twdRate = rateState.data?.rate;
  const displayCurrency = currency === "TWD" && rateState.data ? "TWD" : "USD";
  const formatConvertedMoney = useCallback(
    (value) => formatTwdFromUsd(value, twdRate),
    [twdRate],
  );
  const formatMoney =
    displayCurrency === "TWD" ? formatConvertedMoney : formatUsdWithCode;
  const canEditLimits = hasRole("billing.limits.manage");
  const canEditEnforcement = hasRole("billing.enforcement.manage");

  // null = the current cycle; otherwise the start date of a past one.
  const [cycle, setCycle] = useState(null);
  // Learned from the first overview: the backend owns the calendar.
  const [currentCycle, setCurrentCycle] = useState(null);
  const [overview, setOverview] = useState(null);
  const [overviewError, setOverviewError] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [weekly, setWeekly] = useState(null);
  const [weeklyError, setWeeklyError] = useState(null);
  const [bots, setBots] = useState(null);
  const [botsError, setBotsError] = useState(null);
  const [enforcement, setEnforcement] = useState(null);
  const [enforcementError, setEnforcementError] = useState(null);
  // The users table's filters live here so a click on a group row can set them.
  const [usersQuery, setUsersQuery] = useState({
    groupId: "",
    search: "",
    page: 1,
  });
  const [usersRefresh, setUsersRefresh] = useState(0);
  const [selectedUid, setSelectedUid] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const usersRef = useRef(null);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const token = await currentUser.getIdToken();
      const query = cycle ? `?cycle=${cycle}` : "";
      const response = await fetch(
        `${getApiUrl()}/api/v2/usage-billing/admin/overview${query}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // A proxy error page (a 502 from Cloud Run, say) isn't JSON.
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }
      setOverview(data);
      if (!cycle) setCurrentCycle(data.cycle_start);
    } catch (err) {
      console.error("Failed to load usage overview:", err);
      setOverviewError(err.message);
    } finally {
      setOverviewLoading(false);
    }
  }, [currentUser, cycle]);

  // Spend per week across everyone, ending with the selected cycle.
  const loadWeekly = useCallback(async () => {
    setWeeklyError(null);
    try {
      const token = await currentUser.getIdToken();
      const query = cycle ? `?cycle=${cycle}` : "";
      const response = await fetch(
        `${getApiUrl()}/api/v2/usage-billing/admin/weekly${query}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }
      setWeekly(data);
    } catch (err) {
      console.error("Failed to load weekly usage:", err);
      setWeeklyError(err.message);
    }
  }, [currentUser, cycle]);

  // Every website chatbot's spend in the selected cycle, with its own limit.
  const loadBots = useCallback(async () => {
    setBotsError(null);
    try {
      const token = await currentUser.getIdToken();
      const query = cycle ? `?cycle=${cycle}` : "";
      const response = await fetch(
        `${getApiUrl()}/api/v2/usage-billing/admin/bots${query}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }
      setBots(data.bots);
    } catch (err) {
      console.error("Failed to load chatbot usage:", err);
      setBotsError(err.message);
    }
  }, [currentUser, cycle]);

  const loadEnforcement = useCallback(async () => {
    setEnforcementError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${getApiUrl()}/api/v2/usage-billing/admin/enforcement`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }
      setEnforcement(data);
    } catch (err) {
      console.error("Failed to load usage enforcement:", err);
      setEnforcementError(err.message);
    }
  }, [currentUser]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    loadWeekly();
  }, [loadWeekly]);

  useEffect(() => {
    loadBots();
  }, [loadBots]);

  useEffect(() => {
    loadEnforcement();
  }, [loadEnforcement]);

  // After any change, everything that shows a limit or the switch reloads.
  const refreshAll = () => {
    rateState.refresh();
    loadOverview();
    loadWeekly();
    loadBots();
    loadEnforcement();
    setUsersRefresh((n) => n + 1);
  };

  const changeCycle = (start) => {
    setCycle(start === currentCycle ? null : start);
    setUsersQuery((q) => ({ ...q, page: 1 }));
  };

  const filterUsersByGroup = (groupId) => {
    setUsersQuery({ groupId, search: "", page: 1 });
    usersRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const isCurrent = Boolean(overview?.current_week);
  const groups = overview?.groups || [];
  const overQuotaUsers = isCurrent
    ? groups.reduce((sum, g) => sum + (g.over_quota_users || 0), 0)
    : null;

  return (
    <div className="space-y-6">
      {/* Header: title, cycle picker, refresh */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-gray-900">
              {zh ? "用量與計費" : "Usage & Billing"}
            </h2>
            {!canEditLimits && !canEditEnforcement && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                {zh ? "僅檢視" : "View only"}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {zh
              ? "TG PIMS Agent 與網站聊天機器人的 AI 用量。每月額度以 28 天為一個週期，按週分配。"
              : "AI use in TG PIMS Agent and the website chatbots. Monthly limits run per 28-day cycle and are paced weekly."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600" htmlFor="usage-cycle">
            {zh ? "週期" : "Cycle"}
          </label>
          <select
            id="usage-cycle"
            value={cycle || currentCycle || ""}
            onChange={(e) => changeCycle(e.target.value)}
            disabled={!currentCycle}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 disabled:bg-gray-100"
          >
            {currentCycle &&
              cyclesUpTo(currentCycle).map(({ start, end }) => {
                const range = `${formatShortDay(start, language)} – ${formatShortDay(end, language)}`;
                const label = zh
                  ? `第 ${cycleNumber(start)} 週期 · ${range}${start === currentCycle ? "（本週期）" : ""}`
                  : `Cycle ${cycleNumber(start)} · ${range}${start === currentCycle ? " (current)" : ""}`;
                return (
                  <option key={start} value={start}>
                    {label}
                  </option>
                );
              })}
          </select>
          <button
            type="button"
            onClick={refreshAll}
            disabled={overviewLoading}
            className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            title={zh ? "重新整理" : "Refresh"}
            aria-label={zh ? "重新整理" : "Refresh"}
          >
            <FiRefreshCw
              className={`w-4 h-4 ${overviewLoading ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      </div>

      <UsageCurrencySwitch
        currency={displayCurrency}
        onChange={setCurrency}
        rateState={rateState}
        language={language}
      />

      <EnforcementCard
        enforcement={enforcement}
        error={enforcementError}
        onRetry={loadEnforcement}
        overQuotaUsers={overQuotaUsers}
        canEdit={canEditEnforcement}
        currentUser={currentUser}
        onChanged={refreshAll}
        language={language}
      />

      {overviewError && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>
            {zh ? "無法載入用量總覽：" : "Couldn't load the usage overview: "}
            {overviewError}
          </span>
          <button
            type="button"
            onClick={loadOverview}
            className="shrink-0 font-medium underline"
          >
            {zh ? "重試" : "Retry"}
          </button>
        </div>
      )}

      {!overview && overviewLoading && (
        <div className="flex flex-col items-center gap-3 py-16">
          <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
          <p className="text-sm text-gray-500">
            {zh ? "正在載入用量..." : "Loading usage..."}
          </p>
        </div>
      )}

      {/* On a reload the last result stays, dimmed, rather than flashing. */}
      {overview && (
        <div
          className={`space-y-6 transition-opacity ${overviewLoading ? "opacity-60" : ""}`}
        >
          <UsageTotals
            overview={overview}
            overQuotaUsers={overQuotaUsers}
            weekly={weekly}
            weeklyError={weeklyError}
            onRetryWeekly={loadWeekly}
            formatMoney={formatMoney}
            language={language}
          />
          <GroupSpendTable
            groups={groups}
            isCurrent={isCurrent}
            canEdit={canEditLimits}
            currentUser={currentUser}
            onSaved={refreshAll}
            onFilterGroup={filterUsersByGroup}
            formatMoney={formatMoney}
            currency={displayCurrency}
            rateState={rateState}
            language={language}
          />
        </div>
      )}

      <BotSpendTable
        bots={bots}
        error={botsError}
        onRetry={loadBots}
        isCurrent={cycle === null}
        enforced={Boolean(overview?.enforced)}
        canEdit={canEditLimits}
        currentUser={currentUser}
        onSaved={refreshAll}
        formatMoney={formatMoney}
        currency={displayCurrency}
        rateState={rateState}
        language={language}
      />

      <div ref={usersRef} className="scroll-mt-4">
        <UsageUsersTable
          cycle={cycle}
          groups={groups}
          query={usersQuery}
          onQueryChange={setUsersQuery}
          isCurrent={isCurrent}
          enforced={Boolean(overview?.enforced)}
          refreshKey={usersRefresh}
          formatMoney={formatMoney}
          onSelectUser={setSelectedUid}
          onPickUser={() => setPickerOpen(true)}
          canEdit={canEditLimits}
          currentUser={currentUser}
          language={language}
        />
      </div>

      {overview && (
        <UnpricedCallsTable
          rows={overview.unpriced_calls}
          language={language}
        />
      )}

      {selectedUid && (
        <UserUsageModal
          uid={selectedUid}
          groups={groups}
          canEdit={canEditLimits}
          currentUser={currentUser}
          onClose={() => setSelectedUid(null)}
          formatMoney={formatMoney}
          currency={displayCurrency}
          onCurrencyChange={setCurrency}
          rateState={rateState}
          onSaved={refreshAll}
          language={language}
        />
      )}

      {pickerOpen && (
        <UserPickerModal
          onPick={(uid) => {
            setPickerOpen(false);
            setSelectedUid(uid);
          }}
          onClose={() => setPickerOpen(false)}
          language={language}
        />
      )}
    </div>
  );
}
